import {
  Box,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerOverlay,
  Flex,
  IconButton,
  Image,
  Text,
  VStack,
  Button,
} from "@chakra-ui/react";
import { ChevronRightIcon, CloseIcon, HamburgerIcon } from "@chakra-ui/icons";
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useDisclosure } from "@chakra-ui/react";
import { useDispatch, useSelector } from "react-redux";
import logo from "assets/img/layout/logo.svg";
import SidebarBanner from "components/sidebar/components/SidebarBanner";
import { logoutRequest } from "store/user/reducer";
import { userSelector } from "store/user/selector";
import { clearDraftStorage } from "views/customer/order/utils/draftStorage";
import React from "react";

type CustomerMenuRoute = {
  name: string;
  layout: string;
  path: string;
  hidden?: boolean;
};

const ALLOWED_ROUTES = [
  "Dashboard",
  "Projects",
  "Services",
  "Support",
  "Settings",
];

export default function CustomerMobileMenu(props: {
  routes: CustomerMenuRoute[];
}) {
  const { routes } = props;
  const { isOpen, onOpen, onClose } = useDisclosure();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector(userSelector);
  const baseUrl = process.env.REACT_APP_API_BASE_URL;

  const handleLogout = () => {
    localStorage.removeItem("access_token");
    localStorage.removeItem("user_details");
    dispatch(logoutRequest());
    navigate("/auth/sign-in/");
    onClose();
  };

  const menuRoutes = useMemo(() => {
    return routes.filter(
      (r) =>
        r.layout === "/customer" &&
        !r.hidden &&
        ALLOWED_ROUTES.includes(r.name),
    );
  }, [routes]);

  const userAvatar = React.useMemo(() => {
    if (user) {
      return user.profile_pic_src
        ? user.profile_pic_src?.includes("http")
          ? user.profile_pic_src
          : `${baseUrl}/public_html/images/${user.profile_pic_src}`
        : null;
    }
    return null;
  }, [user]);

  const userName = React.useMemo(() => {
    if (user) {
      return `${user.first_name} ${user.last_name}`;
    }
    return "";
  }, [user]);

  return (
    <>
      <Box
        display={{ base: "block", xl: "none" }}
        position="fixed"
        top="0"
        left="0"
        right="0"
        h="72px"
        bg="#FFFFFF"
        borderBottom="1px solid #E5E7EB"
        zIndex="300"
      >
        <Flex
          h="100%"
          alignItems="center"
          justifyContent="space-between"
          px="24px"
        >
          <Image src={logo} alt="Languex" h="32px" w="auto" />
          <IconButton
            aria-label={isOpen ? "Close menu" : "Open menu"}
            onClick={isOpen ? onClose : onOpen}
            icon={isOpen ? <CloseIcon /> : <HamburgerIcon />}
            variant="ghost"
            color="#111827"
            size="lg"
          />
        </Flex>
      </Box>

      <Drawer isOpen={isOpen} placement="left" onClose={onClose} size="full">
        <DrawerOverlay bg="rgba(0, 0, 0, 0.4)" />
        <DrawerContent bg="#FFFFFF" p="0" display="flex" flexDirection="column">
          <Flex
            alignItems="center"
            justifyContent="space-between"
            px="24px"
            h="72px"
            borderBottom="1px solid #E5E7EB"
          >
            <Image src={logo} alt="Languex" h="32px" w="auto" />
            <IconButton
              aria-label="Close menu"
              onClick={onClose}
              icon={<CloseIcon />}
              variant="ghost"
              color="#0B0E10"
              size="lg"
            />
          </Flex>

          <DrawerBody p="0" flex="1" display="flex" flexDirection="column">
            <Box flex="0 0 auto">
              <VStack align="stretch" spacing="0">
                {menuRoutes.map((route) => {
                  const to = route.layout + route.path;
                  return (
                    <Flex
                      key={to}
                      as="button"
                      type="button"
                      alignItems="center"
                      justifyContent="space-between"
                      px="24px"
                      py="16px"
                      // borderBottom="1px solid #F3F4F6"
                      onClick={() => {
                        navigate(to);
                        onClose();
                      }}
                    >
                      <Text
                        fontSize="18px"
                        // lineHeight="21px"
                        fontWeight="600"
                        color="#0B0E10"
                      >
                        {route.name}
                      </Text>
                      <ChevronRightIcon boxSize="28px" color="#111827" />
                    </Flex>
                  );
                })}
              </VStack>
            </Box>

            <Flex
              flex="1 1 auto"
              bg="#EBF0FB"
              px="24px"
              py="24px"
              m="24px"
              alignItems="center"
              justifyContent="center"
              borderRadius="12px"
            >
              <SidebarBanner
                onStartOrder={() => {
                  clearDraftStorage(user?.user_id);
                  navigate("/customer/main");
                  onClose();
                }}
              />
            </Flex>

            <Box px="24px" pb="24px">
              <Button
                w="100%"
                h="48px"
                bg="transparent"
                border="1px solid #E5E7EB"
                borderRadius="8px"
                color="#0B0E10"
                fontSize="16px"
                fontWeight="600"
                _hover={{ bg: "#F9FAFB" }}
                onClick={handleLogout}
              >
                Logout
              </Button>
            </Box>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </>
  );
}
